// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import numeral from 'numeral';
import glamorous, { Div, Span } from 'glamorous';
import { clamp, first, last } from 'lodash';
import colorUtils from 'color';
import t from 'app/utils/translate';
import Avatar from 'app/components/commons/Avatar';
import RewriteLink from 'app/components/routing/RewriteLink';
import Tappable from 'app/components/commons/Tappable';
import getGlobalLeaderboardColor from 'app/utils/getGlobalLeaderboardColor';
import getGlobalLeaderboardScale from 'app/utils/getGlobalLeaderboardScale';
import ChangePropsOnMount from 'app/components/commons/ChangePropsOnMount';
import * as cssEase from 'app/constants/cssEase';
import type { OriginalEntry } from 'app/normalizers/decodeUltimateChallenge';

type Props = {
  avatar: Object,
  highestScore: number,
  lowScore: number,
  name: string,
  numEntries: number,
  originalEntries: Array<OriginalEntry>,
  rank: number,
  score: number,
};

const MyScoreBlockDiv = glamorous.div(props => ({
  backgroundColor: colorUtils(props.theme.color.panel).alpha(0.7).string(),
  display: 'flex',
  flexDirection: 'column',
  color: props.theme.color.basis6,
}));

const MeterWrapper = glamorous.div({
  height: 40,
  width: '100%',
  position: 'relative',
  borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
  marginBottom: 20,
});

const Meter = glamorous.div(props => ({
  backgroundColor: props.color,
  height: '100%',
  width: '100%',
  transform: `scaleX(${clamp(props.percent, 0, 1)}) translateZ(0)`,
  transformOrigin: 'left',
  transition: 'transform 1s',
  transitionTimingFunction: cssEase.Strong.easeOut,
}));

const UserDetails = glamorous.div(props => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'absolute',
  height: '100%',
  width: '100%',
  fontSize: props.theme.fontSize.small,
  padding: props.theme.spacing.small,
  top: 0,
  left: 0,
}));

const Text = glamorous.span(props => ({
  fontWeight: props.bold ? props.theme.fontWeight.bold : props.theme.fontWeight.normal,
  padding: 4,
}));

const Divider = glamorous.hr({
  marginBottom: 8,
  marginTop: 8,
  borderColor: 'rgba(255, 255, 255, 0.3)',
  width: '50%',
});

const Content = glamorous.div(props => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: props.theme.fontSize.small,
  fontWeight: props.theme.fontWeight.normal,
}));

const SmallDetails = glamorous.div(props => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: props.theme.fontSize.tiny,
  textTransform: 'uppercase',
}));

const Bold = glamorous.span(props => ({
  fontWeight: props.theme.fontWeight.demiBold,
  paddingRight: 5,
}));

const FlexDiv = glamorous.div({
  display: 'flex',
  padding: 4,
});

const ConstrainedDiv = glamorous.div({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}, props => ({
  maxWidth: props.width,
}));

const LinkButton = glamorous(Tappable)({
  overflow: 'hidden',
  display: 'flex',
});

function MyScoreBlock({
  avatar,
  highestScore,
  lowScore,
  name,
  numEntries,
  originalEntries,
  rank,
  score,
}: Props) {
  return (
    <MyScoreBlockDiv>
      <MeterWrapper>
        <ChangePropsOnMount
          delay={0.2}
          beforeProps={{
            percent: 0,
          }}
          afterProps={{
            percent: getGlobalLeaderboardScale(score, lowScore, highestScore),
          }}
        >
          <Meter color={getGlobalLeaderboardColor(clamp(rank, 1, 100), numEntries)} />
        </ChangePropsOnMount>
        <UserDetails>
          <Text>#{rank}</Text>
          <Text>{numeral(score).format('0.00')}</Text>
        </UserDetails>
      </MeterWrapper>
      <Content>
        <Avatar avatar={avatar} size="medium" />
        <Span paddingTop={6}>{name}</Span>
        <Divider />
        {originalEntries.map(entry => (
          <SmallDetails key={entry.id}>
            <FlexDiv>
              <Bold>{t('Match')}:</Bold>
              <LinkButton
                component={RewriteLink}
                to={`/challenge/${entry.challengeId}`}
              >
                <ConstrainedDiv width="100%">{entry.challengeName}</ConstrainedDiv>
                <Span paddingLeft={4} className="ion-android-open" />
              </LinkButton>
            </FlexDiv>
            <Div display="flex">
              <FlexDiv>
                <Bold>{t('Global Points')}:</Bold>
                <div>{numeral(entry.globalScore).format('0.00')}</div>
              </FlexDiv>
              <FlexDiv>
                <Bold>{t('Position')}:</Bold>
                <Div textTransform="lowercase">{numeral(entry.position).format('0o')}</Div>
              </FlexDiv>
              <FlexDiv>
                <Bold>{t('Date')}:</Bold>
                <div>{moment(entry.challengeStartTime).format('ddd, DD MMM YY')}</div>
              </FlexDiv>
            </Div>
          </SmallDetails>
        ))}
      </Content>
    </MyScoreBlockDiv>
  );
}

function mapStateToProps(state) {
  const entry = state.ultimateChallenge.currentUserEntry;
  return {
    avatar: state.user.currentUser.avatar,
    highestScore: first(state.ultimateChallenge.entries).globalScore,
    lowScore: last(state.ultimateChallenge.entries).globalScore,
    name: state.user.currentUser.name,
    numEntries: state.ultimateChallenge.entries.length,
    originalEntries: entry.originalEntries,
    rank: entry.position,
    score: entry.globalScore,
  };
}

export default connect(mapStateToProps)(MyScoreBlock);
